import { useQuery } from "@tanstack/vue-query";
import { computed, MaybeRef, toRef } from "vue";

import { useFetchSiteInstalledApplianceCategories } from "@/composables/kili/sites/fetches/use-fetch-site-installed-appliance-categories";
import { assertProgramBelongsToSite } from "@/composables/kili/sites/queries/program-query-identity";
import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { SitesQueryKeys } from "@/data/api/kili/sites/sites-query-keys";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { getProgramApplianceCategories } from "@/utils/programs/program-utils";

export function useGetSiteProgramDetailsQuery(
  siteId: MaybeRef<V4HSiteId>,
  programId: MaybeRef<ProgramId>,
  defaultAppliances: readonly EProgramApplianceCategory[],
  blackListedAppliances: readonly EProgramApplianceCategory[] = [],
) {
  const { fetch: fetchInstalledApplianceCategories } = useFetchSiteInstalledApplianceCategories();

  const v4bClientSdk = useServices().v4b.clientSdk;

  const _siteId = toRef(siteId);
  const _programId = toRef(programId);
  const isQueryEnabled = computed(() => _siteId.value > 0 && _programId.value > 0);

  return useQuery({
    queryKey: SitesQueryKeys.programDetails(_siteId, _programId),
    queryFn: async () => {
      const maybeInstalledApplianceCategories = await fetchInstalledApplianceCategories(_siteId.value);

      const applianceCategories = getProgramApplianceCategories(
        defaultAppliances,
        maybeInstalledApplianceCategories,
        blackListedAppliances,
      );

      const program = await v4bClientSdk.siteProgram.getProgramDetails(_programId.value, applianceCategories);

      return assertProgramBelongsToSite(program, _siteId.value);
    },
    enabled: isQueryEnabled,
  });
}
